import { useSession } from '@/api/session';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Phone, Plus } from 'lucide-react';
import { t } from '@/i18n';
import { useDrivers, useSaveDriver } from '@/api/hooks/catalog';
import type { Driver } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import {
  CardList,
  CardListEmpty,
  CardListItem,
  DataRow,
  NarrowOnly,
  WideOnly,
} from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { ExpiryBadge } from '@/components/common/ExpiryBadge';
import { toast } from '@/stores/toast';
import { PhoneCodeButton } from '@/components/dispatch/PhoneCodeDialog';

type Draft = Partial<Driver> & { id?: string };

const STATUS_BG: Record<string, string> = {
  active: 'На работа',
  on_leave: 'В отпуск',
  inactive: 'Неактивен',
};

/** Картата за тахограф се чака ~30 дни от ИААА — предупреждението е на 60. */

export function DriversPage() {
  const { data, isLoading, isError, refetch } = useDrivers();
  const save = useSaveDriver();
  const [draft, setDraft] = useState<Draft | null>(null);
  const isOwner = useSession((st) => st.user?.role) === 'owner';
  const search = useSearch({ from: '/drivers' });
  const navigate = useNavigate();
  /** Дошли от досието с „Данни на шофьора" — формата се отваря сама. */
  useEffect(() => {
    if (!search.edit || !data) return;
    const found = data.items.find((d) => d.id === search.edit);
    if (found) setDraft(found);
    void navigate({ to: '/drivers', search: {}, replace: true });
  }, [search.edit, data, navigate]);

  const submit = () => {
    if (!draft) return;
    save.mutate(draft, {
      onSuccess: () => {
        toast.ok('Шофьорът е записан.');
        setDraft(null);
      },
    });
  };

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.drivers.title}</h1>
        <span className="hidden text-xs text-ink-dim sm:inline">{t.drivers.cardLeadTime}</span>
        {/* Нов бус/шофьор създава само управителят — сървърът отказва на диспечера. */}
        {isOwner ? (
        <Button
            className="ml-auto"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setDraft({ status: 'active' })}
          >
            {t.drivers.newDriver}
          </Button>
        ) : null}
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <>
            {/*
              Тесен екран: първо името и ТЕЛЕФОНЪТ като бутон за набиране —
              шофьорският списък се отваря най-често точно за да се позвъни.
            */}
            <NarrowOnly>
              {(data?.items ?? []).length === 0 ? (
                <CardListEmpty>{t.common.empty}</CardListEmpty>
              ) : (
                <CardList>
                  {(data?.items ?? []).map((driver) => (
                    <CardListItem key={driver.id}>
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <Link
                            to="/drivers/$driverId"
                            params={{ driverId: driver.id }}
                            className="block truncate font-medium hover:text-accent"
                          >
                            {driver.fullName || `${driver.firstName} ${driver.lastName}`}
                          </Link>
                          {driver.homeBase ? (
                            <div className="text-[11px] text-ink-faint">{driver.homeBase}</div>
                          ) : null}
                        </div>
                        <Badge tone={driver.status === 'active' ? 'ok' : 'warn'}>
                          {STATUS_BG[driver.status] ?? driver.status}
                        </Badge>
                      </div>

                      {driver.phone ? (
                        <a
                          href={`tel:${driver.phone}`}
                          data-tap
                          className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md border border-accent/40 bg-accent-soft px-3 font-mono text-sm text-accent"
                        >
                          <Phone className="size-4" />
                          {driver.phone}
                        </a>
                      ) : null}

                      <dl className="mt-2">
                        <DataRow label={t.drivers.licence}>
                          <ExpiryBadge date={driver.licenceExpiresOn} label={t.drivers.licence} />
                        </DataRow>
                        <DataRow label={t.drivers.medical}>
                          <ExpiryBadge date={driver.medicalExpiresOn} label={t.drivers.medical} />
                        </DataRow>
                        <DataRow label={t.drivers.psycho}>
                          <ExpiryBadge date={driver.psychoExpiresOn} label={t.drivers.psycho} />
                        </DataRow>
                        {driver.adrCertificateNo ? (
                          <DataRow label={t.drivers.adr}>
                            <ExpiryBadge date={driver.adrExpiresOn} label={t.drivers.adr} />
                          </DataRow>
                        ) : null}
                      </dl>

                      <PhoneCodeButton driverId={driver.id} driverName={`${driver.firstName} ${driver.lastName}`} />
                      <Button
                        className="mt-2 w-full"
                        variant="secondary"
                        onClick={() => setDraft(driver)}
                      >
                        {t.common.edit}
                      </Button>
                    </CardListItem>
                  ))}
                </CardList>
              )}
            </NarrowOnly>

            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.drivers.name}</TH>
                    <TH>{t.drivers.phone}</TH>
                    <TH>{t.common.status}</TH>
                    <TH>{t.drivers.licence}</TH>
                    <TH>{t.drivers.medical}</TH>
                    <TH>{t.drivers.psycho}</TH>
                    <TH>{t.drivers.adr}</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {(data?.items ?? []).length === 0 ? (
                    <TableEmpty colSpan={8}>{t.common.empty}</TableEmpty>
                  ) : (
                    (data?.items ?? []).map((driver) => (
                      <TR key={driver.id}>
                        <TD>
                          <Link
                            to="/drivers/$driverId"
                            params={{ driverId: driver.id }}
                            className="font-medium hover:text-accent"
                          >
                            {driver.fullName || `${driver.firstName} ${driver.lastName}`}
                          </Link>
                          {driver.homeBase ? (
                            <div className="text-[11px] text-ink-faint">{driver.homeBase}</div>
                          ) : null}
                        </TD>
                        <TD className="font-mono text-xs">
                          {driver.phone ? (
                            <a href={`tel:${driver.phone}`} className="hover:text-accent">
                              {driver.phone}
                            </a>
                          ) : (
                            '—'
                          )}
                        </TD>
                        <TD>
                          <Badge tone={driver.status === 'active' ? 'ok' : 'warn'}>
                            {STATUS_BG[driver.status] ?? driver.status}
                          </Badge>
                        </TD>
                        <TD>
                          <ExpiryBadge date={driver.licenceExpiresOn} label={t.drivers.licence} />
                        </TD>
                        <TD>
                          <ExpiryBadge date={driver.medicalExpiresOn} label={t.drivers.medical} />
                        </TD>
                        <TD>
                          <ExpiryBadge date={driver.psychoExpiresOn} label={t.drivers.psycho} />
                        </TD>
                        <TD>
                          {driver.adrCertificateNo ? (
                            <ExpiryBadge date={driver.adrExpiresOn} label={t.drivers.adr} />
                          ) : (
                            <span className="text-xs text-ink-faint">—</span>
                          )}
                        </TD>
                        <TD className="text-right">
                          <PhoneCodeButton driverId={driver.id} driverName={`${driver.firstName} ${driver.lastName}`} />
                          <Button size="sm" variant="ghost" onClick={() => setDraft(driver)}>
                            {t.common.edit}
                          </Button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </>
        )}
      </Card>

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t.common.edit : t.drivers.newDriver}
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t.common.cancel}
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {t.common.save}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Име" required>
              <Input
                data-autofocus
                value={draft.firstName ?? ''}
                onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
              />
            </Field>
            <Field label="Фамилия" required>
              <Input
                value={draft.lastName ?? ''}
                onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
              />
            </Field>
            <Field label={t.drivers.phone}>
              <Input
                className="font-mono"
                value={draft.phone ?? ''}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
            <Field label={t.common.status}>
              <Select
                value={draft.status ?? 'active'}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as Driver['status'] })}
                options={Object.entries(STATUS_BG).map(([value, label]) => ({ value, label }))}
              />
            </Field>
            <Field label="Номер на свидетелство">
              <Input
                value={draft.licenceNo ?? ''}
                onChange={(e) => setDraft({ ...draft, licenceNo: e.target.value })}
              />
            </Field>
            <Field label="Валидно до">
              <Input
                type="date"
                className="font-mono"
                value={draft.licenceExpiresOn ?? ''}
                onChange={(e) => setDraft({ ...draft, licenceExpiresOn: e.target.value })}
              />
            </Field>
            <Field label="Медицинско до">
              <Input
                type="date"
                className="font-mono"
                value={draft.medicalExpiresOn ?? ''}
                onChange={(e) => setDraft({ ...draft, medicalExpiresOn: e.target.value })}
              />
            </Field>
            <Field label="Психотест до">
              <Input
                type="date"
                className="font-mono"
                value={draft.psychoExpiresOn ?? ''}
                onChange={(e) => setDraft({ ...draft, psychoExpiresOn: e.target.value })}
              />
            </Field>
            <Field label="ADR свидетелство">
              <Input
                value={draft.adrCertificateNo ?? ''}
                onChange={(e) => setDraft({ ...draft, adrCertificateNo: e.target.value })}
              />
            </Field>
            <Field label="ADR до">
              <Input
                type="date"
                className="font-mono"
                value={draft.adrExpiresOn ?? ''}
                onChange={(e) => setDraft({ ...draft, adrExpiresOn: e.target.value })}
              />
            </Field>
            <Field label="Дневна ставка (евроцентове)" className="sm:col-span-2">
              <Input
                type="number"
                min={0}
                className="font-mono tabular"
                value={draft.baseDailyRateMinor ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, baseDailyRateMinor: Number(e.target.value) || null })
                }
              />
            </Field>
            <Field label={t.common.notes} className="sm:col-span-2">
              <Textarea
                rows={2}
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Field>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
